import React, { useState, useEffect } from 'react';
import { 
  Modal, Form, Input, Select, Switch, Button, message, Tooltip, Space
} from 'antd';
import { 
  QuestionCircleOutlined, CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import { updateJob, validateCronExpression } from '@/api/modules/job';
import type { JobItem, JobFormData } from '@/api/modules/job';

interface JobModalProps {
  visible: boolean;
  job: JobItem | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const { TextArea } = Input;
const { Option } = Select;

const JobModal: React.FC<JobModalProps> = ({
  visible,
  job,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [validatingCron, setValidatingCron] = useState(false);
  const [cronValid, setCronValid] = useState<boolean | null>(null);

  // 初始化表单数据
  useEffect(() => {
    if (visible && job) {
      form.resetFields();
      
      form.setFieldsValue({
        jobName: job.jobName,
        jobGroup: job.jobGroup,
        invokeTarget: job.invokeTarget,
        cronExpression: job.cronExpression,
        concurrent: job.concurrent,
        misfirePolicy: job.misfirePolicy,
        status: job.status,
        description: job.description || ''
      });
      // 当编辑时，认为已有的cron表达式是有效的
      setCronValid(true);
    }
  }, [visible, job, form]);

  // 验证cron表达式
  const handleValidateCron = async () => {
    const cronExpression = form.getFieldValue('cronExpression');
    if (!cronExpression) {
      message.warning('请先输入Cron表达式');
      return;
    }
    
    setValidatingCron(true);
    try {
      const res = await validateCronExpression(cronExpression);
      const isValid = (res.data as any)?.data ?? res.data ?? false;
      setCronValid(Boolean(isValid));

      if (isValid) {
        message.success('Cron表达式有效');
      } else {
        message.error('Cron表达式无效');
      }
    } catch (error) {
      console.error('验证Cron表达式失败', error);
      setCronValid(false);
    } finally {
      setValidatingCron(false);
    }
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // 构造提交数据
      const submitData: JobFormData = {
        jobName: values.jobName,
        jobGroup: values.jobGroup,
        invokeTarget: values.invokeTarget,
        cronExpression: values.cronExpression,
        concurrent: values.concurrent,
        misfirePolicy: values.misfirePolicy,
        status: values.status,
        description: values.description
      };

      // 只允许更新现有任务
      if (job) {
        await updateJob(job.id, submitData);
        message.success('更新成功');
      }
      
      onSuccess();
    } catch (error) {
      console.error('提交失败', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={'编辑任务表达式'}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          取消
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
        >
          确定
        </Button>
      ]}
      maskClosable={false}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          name="jobName"
          label="任务名称"
        >
          <Input placeholder="任务名称" maxLength={100} disabled />
        </Form.Item>
        
        <Form.Item
          name="jobGroup"
          label="任务组名"
        >
          <Input placeholder="任务组名" disabled />
        </Form.Item>
        
        <Form.Item
          name="invokeTarget"
          label={
            <span>
              调用目标字符串 
              <Tooltip title="任务类的全限定名，如com.coolbuy.job.SyncSpuDataJob.execute">
                <QuestionCircleOutlined style={{ marginLeft: 4 }} />
              </Tooltip>
            </span>
          }
        >
          <Input placeholder="调用目标字符串" disabled />
        </Form.Item>
        
        <Form.Item
          name="cronExpression"
          label={
            <span>
              Cron表达式 
              <Tooltip title="Cron表达式例如：0 0 12 * * ? 表示每天中午12点触发">
                <QuestionCircleOutlined style={{ marginLeft: 4 }} />
              </Tooltip>
            </span>
          }
          rules={[{ required: true, message: '请输入Cron表达式' }]}
          extra={
            cronValid !== null && (
              <span style={{ color: cronValid ? '#52c41a' : '#ff4d4f' }}>
                {cronValid ? (
                  <><CheckCircleOutlined /> Cron表达式有效</>
                ) : (
                  <><CloseCircleOutlined /> Cron表达式无效</>
                )}
              </span>
            )
          }
        >
          <Space.Compact style={{ width: '100%' }}>
            <Input 
              placeholder="请输入Cron表达式，如: 0 0 12 * * ?"
              onChange={() => cronValid !== null && setCronValid(null)} 
            />
            <Button 
              onClick={handleValidateCron} 
              loading={validatingCron}
            >
              验证
            </Button>
          </Space.Compact>
        </Form.Item>
        
        <Form.Item
          name="misfirePolicy"
          label="计划执行错误策略"
          rules={[{ required: true, message: '请选择执行策略' }]}
          tooltip="当任务在预定时间无法执行（如系统宕机）时，系统如何处理这些错过的执行"
        >
          <Select placeholder="请选择执行策略">
            <Option value="default">执行一次（默认策略：执行一次最近错过的任务）</Option>
            <Option value="ignore">忽略（完全忽略所有错过的执行，按当前时间开始新的调度）</Option>
            <Option value="fire_once">立即执行一次（立即执行一次，然后按照正常调度继续）</Option>
          </Select>
        </Form.Item>
        
        <Form.Item
          name="concurrent"
          label="是否允许并发执行"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        
        <Form.Item
          name="status"
          label="状态"
          rules={[{ required: true, message: '请选择状态' }]}
        >
          <Select placeholder="请选择状态">
            <Option value="running">运行中</Option>
            <Option value="paused">已暂停</Option>
          </Select>
        </Form.Item>
        
        <Form.Item
          name="description"
          label="任务描述"
        >
          <TextArea 
            rows={4} 
            placeholder="请输入任务描述信息"
            maxLength={500}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default JobModal;
